import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FiType, FiSave, FiRotateCcw, FiInfo, FiToggleRight } from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { SiteSettings } from '../../../types/content'
import FontPicker from '../FontPicker'
import { toast } from 'react-hot-toast'
import { ORIGINAL_SITE_SETTINGS, ORIGINAL_BRAND_COLORS, isUsingOriginalColors } from '../../../constants/designSystem'

interface SiteSettingsEditorProps {
  onPreview?: (data: SiteSettings) => void
}

// Original site design values are now imported from the centralized design system

const SiteSettingsEditor: React.FC<SiteSettingsEditorProps> = ({ onPreview }) => {
  const { content, loading, saving, saveContent } = useContent('site-settings')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showOriginalValues, setShowOriginalValues] = useState(false)

  const { handleSubmit, watch, reset, setValue, register } = useForm<SiteSettings>({
    defaultValues: {
      ...ORIGINAL_SITE_SETTINGS,
      fonts: {
        ...ORIGINAL_SITE_SETTINGS.fonts,
        fontWeights: {
          body: 400,
          headings: 400,
          buttons: 400
        }
      },
      features: {
        contactForm: false,
        pricingGuide: false
      }
    }
  })

  const watchedData = watch()

  // Load content when available
  useEffect(() => {
    if (content?.data) {
      const siteData = content.data as SiteSettings
      reset(siteData)
      setHasUnsavedChanges(false)
    }
  }, [content, reset])

  // Watch for changes
  useEffect(() => {
    if (content?.data) {
      const currentData = JSON.stringify(watchedData)
      const originalData = JSON.stringify(content.data)
      setHasUnsavedChanges(currentData !== originalData)
    }
  }, [watchedData, content])

  // Auto-preview
  useEffect(() => {
    if (hasUnsavedChanges && onPreview) {
      onPreview(watchedData)
    }
  }, [watchedData, hasUnsavedChanges, onPreview])

  const onSubmit = async (data: SiteSettings) => {
    const success = await saveContent(data)
    if (success) {
      setHasUnsavedChanges(false)
      toast.success('Site settings saved successfully!')
    } else {
      toast.error('Failed to save site settings')
    }
  }

  const handleFontChange = (fontType: 'primaryFont' | 'secondaryFont' | 'headingFont', font: string) => {
    setValue(`fonts.${fontType}`, font)
  }

  const resetToOriginal = () => {
    if (window.confirm('Are you sure you want to reset all settings to the original site design? This cannot be undone.')) {
      reset(ORIGINAL_SITE_SETTINGS)
      toast.success('Settings reset to original design!')
    }
  }

  const isUsingOriginalSettings = () => {
    if (!watchedData.theme) return false
    return isUsingOriginalColors(watchedData.theme.primaryColor, watchedData.theme.secondaryColor) &&
           JSON.stringify(watchedData.fonts) === JSON.stringify(ORIGINAL_SITE_SETTINGS.fonts)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading site settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900">Site Settings</h2>
          <p className="text-sm text-neutral-600">Customize fonts and theme colors for your site</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {hasUnsavedChanges && (
            <div className="flex items-center space-x-2 text-amber-600 text-sm">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span>Unsaved changes</span>
            </div>
          )}
          
          <button
            type="button"
            onClick={() => setShowOriginalValues(!showOriginalValues)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <FiInfo size={16} />
            <span>{showOriginalValues ? 'Hide' : 'Show'} Original Values</span>
          </button>
        </div>
      </div>

      {/* Original Values Reference */}
      {showOriginalValues && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FiInfo className="text-blue-600 mt-0.5" size={16} />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Original Site Design</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <div><strong>Fonts:</strong> Inter (all typography)</div>
                <div><strong>Primary Color:</strong> {ORIGINAL_BRAND_COLORS.primary} (Dark blue-green)</div>
                <div><strong>Secondary Color:</strong> {ORIGINAL_BRAND_COLORS.secondary} (Light blue-gray)</div>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={resetToOriginal}
                  disabled={isUsingOriginalSettings()}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiRotateCcw size={14} />
                  <span>{isUsingOriginalSettings() ? 'Using Original Settings' : 'Reset to Original'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Font Settings */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiType size={18} />
              <span>Typography</span>
            </h3>
            <div className="text-xs text-neutral-500">
              Original: Inter for all fonts
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FontPicker
              value={watchedData.fonts?.primaryFont || 'Inter'}
              onChange={(font) => handleFontChange('primaryFont', font)}
              label="Primary Font (Body Text)"
              category="sans-serif"
            />
            
            <FontPicker
              value={watchedData.fonts?.secondaryFont || 'Inter'}
              onChange={(font) => handleFontChange('secondaryFont', font)}
              label="Secondary Font (UI Elements)"
              category="sans-serif"
            />
            
            <FontPicker
              value={watchedData.fonts?.headingFont || 'Inter'}
              onChange={(font) => handleFontChange('headingFont', font)}
              label="Heading Font (Titles & Headers)"
              category="display"
            />
          </div>

          {/* Font Weight Settings */}
          <div className="mt-8 pt-6 border-t border-neutral-200">
            <h4 className="text-md font-medium text-neutral-800 mb-4">Font Weights</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Body Text Weight
                </label>
                <select
                  {...register('fonts.fontWeights.body')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value={300}>Light (300)</option>
                  <option value={400}>Normal (400)</option>
                  <option value={500}>Medium (500)</option>
                  <option value={600}>Semi-bold (600)</option>
                  <option value={700}>Bold (700)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Headings Weight
                </label>
                <select
                  {...register('fonts.fontWeights.headings')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value={300}>Light (300)</option>
                  <option value={400}>Normal (400)</option>
                  <option value={500}>Medium (500)</option>
                  <option value={600}>Semi-bold (600)</option>
                  <option value={700}>Bold (700)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Buttons Weight
                </label>
                <select
                  {...register('fonts.fontWeights.buttons')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value={300}>Light (300)</option>
                  <option value={400}>Normal (400)</option>
                  <option value={500}>Medium (500)</option>
                  <option value={600}>Semi-bold (600)</option>
                  <option value={700}>Bold (700)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiType size={18} />
              <span>Theme Colors</span>
            </h3>
            <div className="text-xs text-neutral-500">
              Original: Black (#000000) & Gray (#6B7280)
            </div>
          </div>

          {/* Color System Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <FiInfo className="text-blue-600 mt-0.5" size={16} />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-800 mb-1">Site Color System</h4>
                <p className="text-sm text-blue-700">
                  The site uses these <strong>2 base colors</strong> to generate all other colors through opacity and mixing:
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li>• <strong>Primary:</strong> Main buttons, links, accents</li>
                  <li>• <strong>Secondary:</strong> Text, borders, subtle elements</li>
                  <li>• <strong>Blue tones:</strong> Generated from primary + secondary mixing</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Primary Color (Main Brand Color)
                <span className="text-xs text-neutral-500 ml-2">(Original: {ORIGINAL_SITE_SETTINGS.theme.primaryColor})</span>
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={watchedData.theme?.primaryColor || ORIGINAL_SITE_SETTINGS.theme.primaryColor}
                  onChange={(e) => setValue('theme.primaryColor', e.target.value)}
                  className="w-12 h-10 border border-neutral-300 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={watchedData.theme?.primaryColor || ORIGINAL_SITE_SETTINGS.theme.primaryColor}
                  onChange={(e) => setValue('theme.primaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm font-mono"
                  placeholder={ORIGINAL_SITE_SETTINGS.theme.primaryColor}
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1">Used for buttons, links, and key accents</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Secondary Color (Supporting Color)
                <span className="text-xs text-neutral-500 ml-2">(Original: {ORIGINAL_SITE_SETTINGS.theme.secondaryColor})</span>
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={watchedData.theme?.secondaryColor || ORIGINAL_SITE_SETTINGS.theme.secondaryColor}
                  onChange={(e) => setValue('theme.secondaryColor', e.target.value)}
                  className="w-12 h-10 border border-neutral-300 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={watchedData.theme?.secondaryColor || ORIGINAL_SITE_SETTINGS.theme.secondaryColor}
                  onChange={(e) => setValue('theme.secondaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm font-mono"
                  placeholder={ORIGINAL_SITE_SETTINGS.theme.secondaryColor}
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1">Used for text, borders, and subtle elements</p>
            </div>
          </div>

          {/* Color Preview */}
          <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
            <h4 className="text-sm font-medium text-neutral-700 mb-3">Live Preview</h4>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-6 h-6 rounded border border-neutral-300"
                  style={{ backgroundColor: watchedData.theme?.primaryColor || ORIGINAL_SITE_SETTINGS.theme.primaryColor }}
                ></div>
                <span className="text-sm text-neutral-600">Primary</span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-6 h-6 rounded border border-neutral-300"
                  style={{ backgroundColor: watchedData.theme?.secondaryColor || ORIGINAL_SITE_SETTINGS.theme.secondaryColor }}
                ></div>
                <span className="text-sm text-neutral-600">Secondary</span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-6 h-6 rounded border border-neutral-300"
                  style={{ 
                    background: `linear-gradient(45deg, ${watchedData.theme?.primaryColor || ORIGINAL_SITE_SETTINGS.theme.primaryColor}, ${watchedData.theme?.secondaryColor || ORIGINAL_SITE_SETTINGS.theme.secondaryColor})`
                  }}
                ></div>
                <span className="text-sm text-neutral-600">Mixed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Flags */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiToggleRight size={18} />
              <span>Feature Flags</span>
            </h3>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <FiInfo className="text-amber-600 mt-0.5" size={16} />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-amber-800 mb-1">Control Site Sections</h4>
                <p className="text-sm text-amber-700">
                  Toggle sections on/off without code changes. Disabled sections will be hidden from the website and navigation menu.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Contact Form Toggle */}
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-neutral-800 mb-1">Contact Form</h4>
                <p className="text-xs text-neutral-600">
                  Show/hide the contact form section and "Contact" navigation link
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  {...register('features.contactForm')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                <span className="ml-3 text-sm font-medium text-neutral-700">
                  {watchedData.features?.contactForm ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>

            {/* Pricing Guide Toggle */}
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-neutral-800 mb-1">Pricing Guide</h4>
                <p className="text-xs text-neutral-600">
                  Show/hide the pricing guide section and "Pricing" navigation link
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  {...register('features.pricingGuide')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                <span className="ml-3 text-sm font-medium text-neutral-700">
                  {watchedData.features?.pricingGuide ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={resetToOriginal}
            disabled={isUsingOriginalSettings()}
            className="flex items-center space-x-2 px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiRotateCcw size={16} />
            <span>Reset to Original</span>
          </button>
          
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave size={16} />
                <span>Save Settings</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default SiteSettingsEditor
